import React, { useEffect, useState } from 'react';
import { Button, Card, Dropdown, DropdownButton, OverlayTrigger, Row, Spinner, Table, Tooltip } from 'react-bootstrap';
import { Pagination } from '../../../shared/components/Pagination';
import { InvoiceService } from '../InvoicesService';
import { downloadFileRequest } from '../../../api/services';
import { INVOICE_LIST_DOWNLOAD_URL } from '../../../utils/Endpoints';
import { CampaignsService } from '../../campaigns/CampaignsService';
import { Campaign } from '../../../model/campaign/Campaign';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { Offer } from '../../../model/offer/Offer';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { formatDate, getCorrectEnums, hasAccessRight } from '../../../utils/Functions';
import { OfferStatusEnum } from '../../../enum/OfferStatusEnum';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import { Company } from '../../../model/company/Company';
import { CompanyService } from '../../company/CompanyService';
import CustomFancybox from '../../../shared/components/Fancybox';
import { IUser } from '../../../model/user/IUser';
import { UserService } from '../../user/UserService';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import { useNavigate } from 'react-router';
import { ErrorToast } from '../../../utils/toasters';
import { useDispatch } from 'react-redux';
import generalSlice from '../../../store/general/GeneralSlice';
import { FiInfo } from 'react-icons/fi';
import { IPackages } from '../../../model/package/IPackage';
import { PackagesService } from '../../packages/PackagesService';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';
import ToggleChip from '../../../shared/components/ToggleChip';
import SingleSelectDropdown, { toSelectValue } from '../../../shared/components/SingleSelectDropdown';

const INVOICE_COLUMNS: ITableColumn[] = [
    { label: 'Company' },
    { label: 'Campaign name' },
    { label: 'Package' },
    { label: 'Influencer name' },
    { label: 'Agency name' },
    { label: 'Offer ID' },
    { label: 'PO number' },
    { label: 'Influencer payout' },
    { label: 'influencer Fee' },
    { label: 'Total' },
    { label: 'Reporting completed' },
    { label: 'Invoice uploaded' },
    { label: 'Under discussion?' },
    { label: 'Invoice' },
    { label: 'Payout deadline' },
    { label: 'Paid' },
    { label: 'Paid date' },
    { label: 'Actions' },
];

export default function InvoicesListV2() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 15,
        isPaid: undefined,
        offerStatus: '',
        packageId: undefined,
        campaignId: undefined,
        companyId: undefined,
        hasInvoice: 'true',
        hasInfluencerFee: undefined,
        userId: undefined,
        isUnderDiscussion: undefined,
    });
    const [totalPages, setTotalPages] = useState(0);
    const [offerList, setInvoiceList] = useState<Offer[]>([]);
    const [triggerSearch, setTriggerSearch] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [companyFilter, setCompanyFilter] = useState('');
    const [campaignFilter, setCampaignFilter] = useState('');
    const [influencerFilter, setInfluencerFilter] = useState('');
    const [companyDropdown, setCompanyDropdown] = useState<Company[]>([]);
    const [campaignsDropdown, setCampaignsDropdown] = useState<Campaign[]>([]);
    const [influencerDropdown, setInfluencerDropdown] = useState<IUser[]>([]);
    const [packagesList, setPackagesList] = useState<IPackages[]>([]);

    useEffect(() => {
        CompanyService.getCompanies({ displayNameOrId: companyFilter, perPage: 15 })
            .then(data => {
                setCompanyDropdown(data.data);
            })
            .catch(err => ErrorToast(err));

        PackagesService.getPackages({ page: 1, perPage: 99999999 })
            .then(data => {
                setPackagesList(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [companyFilter]);

    useEffect(() => {
        CampaignsService.getCampaigns({ perPage: 15, title: campaignFilter, isLongTerm: false })
            .then(data => {
                setCampaignsDropdown(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [campaignFilter]);

    useEffect(() => {
        UserService.getUsers({ perPage: 500, userType: 'influencer', displayName: influencerFilter })
            .then(data => {
                setInfluencerDropdown(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [influencerFilter]);

    useEffect(() => {
        setDataLoading(true);
        InvoiceService.getInvoicesV2({ ...pagination })
            .then(data => {
                setInvoiceList(data.data);
                setTotalPages(Math.ceil(data.count / pagination.perPage));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [triggerSearch]);

    const handlePages = (updatePage: number) => {
        setPagination({ ...pagination, page: updatePage });
        setTriggerSearch(!triggerSearch);
    };

    // const doDownload = async (id: number) => {
    // 	try {
    // 		downloadFileRequest(`${INVOICING_URL}/download/${id}`, 'invoice', null, null, setIsLoading);
    // 	} catch (error) {
    // 		ErrorToast(error);
    // 	}
    // };

    const handleVerify = (id: number | undefined) => {
        navigate(`/invoices/verify/edit/${id}`);
    };

    function updateInvoiceField(invoiceId: number, field: 'isPaid' | 'isUnderDiscussion') {
        setInvoiceList(prev =>
            prev.map(offer => ({
                ...offer,
                campaign: {
                    ...offer.campaign,
                    invoices: offer.campaign?.invoices?.map(inv =>
                        inv.id === invoiceId ? { ...inv, [field]: !inv[field] } : inv
                    ),
                },
            }))
        );
    }

    function handleCheckboxPaid(invoiceId: number) {
        InvoiceService.togglePaid(invoiceId)
            .then(() => updateInvoiceField(invoiceId, 'isPaid'))
            .catch(err => ErrorToast(err));
    }

    function handleIsUnderDiscussion(invoiceId: number) {
        InvoiceService.toggleDiscussion(invoiceId)
            .then(() => updateInvoiceField(invoiceId, 'isUnderDiscussion'))
            .catch(err => ErrorToast(err));
    }

    function handleCheckboxProcessed(invoiceId: number) {
        InvoiceService.toggleProcessed(invoiceId);

        const updatedList = offerList.map(offer => {
            const updatedInvoices = offer.campaign?.invoices?.map(inv => {
                if (inv.id === invoiceId) {
                    return { ...inv, isProcessed: !inv.isProcessed };
                }
                return inv;
            });

            return {
                ...offer,
                campaign: {
                    ...offer.campaign,
                    invoices: updatedInvoices,
                },
            };
        });

        setInvoiceList(updatedList);
        dispatch(
            generalSlice.actions.setConfirmModal({
                show: false,
            })
        );
    }

    function handleChange(evt: any) {
        const value = evt.target.value;
        setPagination(prev => ({
            ...prev,
            page: 1,
            [evt.target.name]: value,
        }));
        // setTriggerSearch(!triggerSearch);
    }

    function handleClick() {
        setPagination(prev => ({
            ...prev,
            page: 1,
        }));
        setTriggerSearch(!triggerSearch);
    }

    return (
        <div>
            <div
                className="card mb-5 p-4 mt-3"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick();
                }}
            >
                <Row style={{ rowGap: '18px' }}>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Company name or ID"
                            onSelect={company => {
                                setPagination(prev => ({
                                    ...prev,
                                    companyId: company.id,
                                    page: 1,
                                }));
                                setTriggerSearch(!triggerSearch);
                            }}
                            setFilter={setCompanyFilter}
                            value={companyFilter}
                            items={companyDropdown}
                            hasClearBtn
                            renderItems={['id', 'displayName']}
                            clearFunc={() => {
                                setPagination((prev: any) => ({ ...prev, page: 1, companyId: undefined }));
                                setTriggerSearch(!triggerSearch);
                                setCompanyFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Campaign name or ID"
                            onSelect={campaign => {
                                setPagination(prev => ({
                                    ...prev,
                                    campaignId: campaign.id,
                                    page: 1,
                                }));
                                setTriggerSearch(!triggerSearch);
                            }}
                            setFilter={setCampaignFilter}
                            value={campaignFilter}
                            items={campaignsDropdown}
                            hasClearBtn
                            renderItems={['id', 'title']}
                            clearFunc={() => {
                                setPagination((prev: any) => ({ ...prev, page: 1, campaignId: undefined }));
                                setTriggerSearch(!triggerSearch);
                                setCampaignFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="All Packages"
                            options={packagesList.map(item => ({
                                value: String(item.id),
                                label: getCorrectEnums(item.displayName),
                            }))}
                            selected={toSelectValue((pagination as any).packageId, '')}
                            onChange={value => handleChange({ target: { name: 'packageId', value: value || '' } })}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="is Reporting completed?"
                            options={[
                                { value: 'completed', label: 'Reporting completed' },
                                { value: 'accepted', label: 'Reporting not completed' },
                            ]}
                            selected={toSelectValue((pagination as any).offerStatus, '')}
                            onChange={value => handleChange({ target: { name: 'offerStatus', value: value || '' } })}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="Is Invoice uploaded?"
                            options={[
                                { value: 'true', label: 'Invoice uploaded' },
                                { value: 'false', label: 'Invoice not uploaded' },
                            ]}
                            selected={toSelectValue((pagination as any).hasInvoice, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'hasInvoice', value: value || 'undefined' } })
                            }
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="is Invoice paid?"
                            options={[
                                { value: 'true', label: 'Paid' },
                                { value: 'false', label: 'Not Paid' },
                            ]}
                            selected={toSelectValue((pagination as any).isPaid, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isPaid', value: value || 'undefined' } })
                            }
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="is Invoice verified?"
                            options={[
                                { value: 'true', label: 'Verified' },
                                { value: 'false', label: 'Not Verified' },
                            ]}
                            selected={toSelectValue((pagination as any).isVerified, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isVerified', value: value || 'undefined' } })
                            }
                        />
                    </div>
                    {/*<div className="col-md-2 col-6">*/}
                    {/*    <Form.Select*/}
                    {/*        className="autocomplete-input"*/}
                    {/*        name="isProcessed"*/}
                    {/*        onChange={handleChange}*/}
                    {/*      
                    {/*    >*/}
                    {/*        <option value="undefined">is Invoice processed?</option>*/}
                    {/*        <option value="true">Processed</option>*/}
                    {/*        <option value="false">Not Processed</option>*/}
                    {/*    </Form.Select>*/}
                    {/*</div>*/}
                    <div className="col-md-2 col-6 autocomplete-input">
                        <AutocompleteInput
                            placeholder="Influencer name"
                            onSelect={user => {
                                setPagination(prev => ({
                                    ...prev,
                                    userId: user.id,
                                    page: 1,
                                }));
                                setTriggerSearch(!triggerSearch);
                            }}
                            setFilter={setInfluencerFilter}
                            value={influencerFilter}
                            items={influencerDropdown}
                            hasClearBtn
                            renderItems={['id', 'displayName']}
                            clearFunc={() => {
                                setPagination((prev: any) => ({ ...prev, page: 1, userId: undefined }));
                                setTriggerSearch(!triggerSearch);
                                setInfluencerFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="Has influencer fee?"
                            options={[
                                { value: 'true', label: 'Influencer fee' },
                                { value: 'false', label: 'No influencer fee' },
                            ]}
                            selected={toSelectValue((pagination as any).hasInfluencerFee, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'hasInfluencerFee', value: value || 'undefined' } })
                            }
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="Is under discussion?"
                            options={[
                                { value: 'true', label: 'Under discussion' },
                                { value: 'false', label: 'Not under discussion' },
                            ]}
                            selected={toSelectValue((pagination as any).isUnderDiscussion, 'undefined')}
                            onChange={value =>
                                handleChange({ target: { name: 'isUnderDiscussion', value: value || 'undefined' } })
                            }
                        />
                    </div>
                </Row>
                <div className="d-flex gap-4">
                    <Button className="mt-4" disabled={isLoading} onClick={() => handleClick()}>
                        Search
                    </Button>
                    <Button
                        className="mt-4"
                        variant="outline-primary"
                        onClick={() =>
                            downloadFileRequest(
                                INVOICE_LIST_DOWNLOAD_URL,
                                'export-invoices',
                                null,
                                pagination,
                                setIsLoading
                            )
                        }
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                Generating excel...{' '}
                                <Spinner animation="border" size="sm" role="status" className="ml-1" />
                            </>
                        ) : (
                            'Export current'
                        )}
                    </Button>
                </div>
            </div>
            <Card className="mb-5 p-4 mt-5">
                {dataLoading ? (
                    <TableSkeleton columns={INVOICE_COLUMNS} rows={pagination.perPage} />
                ) : (
                    // nowrap makes the table as wide as its columns, width 0 + min-width keeps that out of the page width
                    <div className="table-responsive" style={{ width: 0, minWidth: '100%' }}>
                        <Table hover style={{ whiteSpace: 'nowrap' }}>
                            <thead>
                                <tr>
                                    {INVOICE_COLUMNS.map(column => (
                                        <th key={column.label} className={column.className}>
                                            {column.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {offerList.map(item => (
                                    <tr key={item.id}>
                                        <td>
                                            {item?.campaign?.company?.displayName}
                                            <div>
                                                <span
                                                    className={`status ${
                                                        item.isLongTerm
                                                            ? 'linked'
                                                            : item.isAddonsOnly
                                                            ? 'negotiate'
                                                            : ''
                                                    }`}
                                                >
                                                    {item.isLongTerm
                                                        ? 'linked'
                                                        : item.isAddonsOnly
                                                        ? 'add-ons'
                                                        : 'standard'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="text-primary">({item?.campaign?.id})</span>
                                            {item?.campaign?.title}
                                        </td>
                                        <td>{item?.campaign?.company?.package?.displayName}</td>
                                        <td>
                                            <ImpersonateEye id={item.user.id ?? 0} />
                                            {item.user.firstName} {item.user.lastName}
                                        </td>
                                        <td>
                                            {item?.user?.agencyCompany?.displayName ? (
                                                <span className="word-break">
                                                    {item?.user?.agencyCompany?.displayName}
                                                </span>
                                            ) : (
                                                <span className="word-break text-danger">
                                                    <FaTimes />
                                                </span>
                                            )}
                                        </td>
                                        <td>{item.id}</td>
                                        <td>{item.purchaseOrderInfluencer}</td>
                                        <td>
                                            {item?.influencerDeducted
                                                ? priceSplitter(item.influencerDeducted)
                                                : item.isLongTerm
                                                ? priceSplitter(item.spendingPrice)
                                                : priceSplitter(item.price)}
                                            €{item.additionalPrice > 0 && `(+${priceSplitter(item.additionalPrice)})€`}
                                        </td>
                                        <td>
                                            {item.hasInfluencerFee ? (
                                                priceSplitter(item.price - item?.influencerDeducted) + '€'
                                            ) : (
                                                <span className="word-break text-danger">
                                                    <FaTimes />
                                                </span>
                                            )}{' '}
                                            {item.hasInfluencerFee && (
                                                <span className="text-primary">({item?.influencerFeePercentage}%)</span>
                                            )}
                                        </td>
                                        {/*<td>*/}
                                        {/*    {item.isLongTerm*/}
                                        {/*        ? priceSplitter(*/}
                                        {/*              Math.round(*/}
                                        {/*                  item?.spendingPrice / item.campaign.feePercentage - item.spendingPrice*/}
                                        {/*              )*/}
                                        {/*          )*/}
                                        {/*        : priceSplitter(*/}
                                        {/*              Math.round(item?.price / item.campaign.feePercentage - item.price)*/}
                                        {/*          )}*/}
                                        {/*    €{' '}*/}
                                        {/*    <span className="text-primary">*/}
                                        {/*        ({calculateFee(item?.campaign?.feePercentage)})*/}
                                        {/*    </span>*/}
                                        {/*</td>*/}
                                        <td>
                                            {item.isLongTerm
                                                ? priceSplitter(
                                                      Math.round(item?.spendingPrice / item.campaign.feePercentage)
                                                  )
                                                : priceSplitter(
                                                      Math.round(
                                                          item?.price / item.campaign.feePercentage +
                                                              item.additionalPrice
                                                      )
                                                  )}
                                            €
                                        </td>
                                        <td>
                                            {item?.status === OfferStatusEnum.COMPLETED ? (
                                                <span className="word-break text-primary">
                                                    <FaCheck />
                                                </span>
                                            ) : (
                                                <span className="word-break text-danger">
                                                    <FaTimes />
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {item?.campaign?.invoices?.[0]?.id ? (
                                                <span className="word-break text-primary">
                                                    <FaCheck />
                                                    {formatDate(item?.campaign?.invoices?.[0]?.date)}
                                                </span>
                                            ) : (
                                                <span className="word-break text-danger">
                                                    <FaTimes />
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {!!item.campaign?.invoices?.length && (
                                                <ToggleChip
                                                    name="isUnderDiscussion"
                                                    disabled={!hasAccessRight(AdminAclEnum.INVOICE_ACTION)}
                                                    onChange={() =>
                                                        handleIsUnderDiscussion(item.campaign?.invoices?.[0]?.id ?? 0)
                                                    }
                                                    checked={!!item.campaign?.invoices?.[0]?.isUnderDiscussion}
                                                    label={
                                                        item.campaign?.invoices?.[0]?.isUnderDiscussion ? 'Yes' : 'No'
                                                    }
                                                />
                                            )}
                                        </td>{' '}
                                        <td>
                                            {item?.campaign?.invoices?.[0]?.id &&
                                            item?.campaign?.invoices?.[0]?.isVerified ? (
                                                <CustomFancybox
                                                    files={item?.campaign?.invoices}
                                                    isView
                                                    variant="outline-primary"
                                                />
                                            ) : item?.campaign?.invoices?.[0]?.id &&
                                              !item?.campaign?.invoices?.[0]?.isVerified ? (
                                                <Button
                                                    type="submit"
                                                    variant="outline-primary"
                                                    onClick={() => handleVerify(item?.campaign?.invoices?.[0]?.id)}
                                                    disabled={!hasAccessRight(AdminAclEnum.INVOICE_VERIFICATION)}
                                                >
                                                    Verify
                                                </Button>
                                            ) : (
                                                <span className="word-break text-danger">
                                                    <FaTimes />
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {item?.paymentDeadlineDescription ? (
                                                <OverlayTrigger
                                                    trigger="click"
                                                    rootClose
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id={`payment-deadline-tooltip-${item.id}`}>
                                                            {item.paymentDeadlineDescription}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span>
                                                        <FiInfo size={16} className="cursor-pointer" />
                                                    </span>
                                                </OverlayTrigger>
                                            ) : item?.paymentDeadline ? (
                                                formatDate(item.paymentDeadline)
                                            ) : (
                                                <span className="word-break text-danger">
                                                    <FaTimes />
                                                </span>
                                            )}
                                        </td>
                                        {
                                            <>
                                                {/*<td>*/}
                                                {/*    {item.campaign?.invoices?.length &&*/}
                                                {/*    item.campaign?.invoices?.[0]?.isVerified ? (*/}
                                                {/*        <div className="d-flex align-items-center">*/}
                                                {/*              <Form.Check
                                        {/*                name="isProcessed"*/}
                                                {/*                disabled={!hasAccessRight(AdminAclEnum.INVOICE_ACTION)}*/}
                                                {/*                onChange={() => {*/}
                                                {/*                    dispatch(*/}
                                                {/*                        generalSlice.actions.setConfirmModal({*/}
                                                {/*                            show: true,*/}
                                                {/*                            title: 'Process Confirmation',*/}
                                                {/*                            contentBody: `Are you sure you want to mark this item as ${*/}
                                                {/*                                item.campaign?.invoices?.[0]?.isProcessed*/}
                                                {/*                                    ? 'unprocessed'*/}
                                                {/*                                    : 'processed'*/}
                                                {/*                            }?`,*/}
                                                {/*                            action: () =>*/}
                                                {/*                                handleCheckboxProcessed(*/}
                                                {/*                                    item.campaign?.invoices?.[0]?.id ?? 0*/}
                                                {/*                                ),*/}
                                                {/*                        })*/}
                                                {/*                    );*/}
                                                {/*                }}*/}
                                                {/*                checked={item.campaign?.invoices?.[0]?.isProcessed}*/}
                                                {/*                data-toggle="toggle"*/}
                                                {/*            />*/}
                                                {/*            <p className="m-0 pl-2"></p>*/}
                                                {/*        </div>*/}
                                                {/*    ) : (*/}
                                                {/*        ''*/}
                                                {/*    )}*/}
                                                {/*</td>*/}
                                                <td>
                                                    {item.campaign?.invoices?.length &&
                                                    item.campaign?.invoices?.[0]?.isVerified ? (
                                                        <ToggleChip
                                                            name="isPaid"
                                                            disabled={!hasAccessRight(AdminAclEnum.INVOICE_ACTION)}
                                                            onChange={() =>
                                                                handleCheckboxPaid(
                                                                    item.campaign?.invoices?.[0]?.id ?? 0
                                                                )
                                                            }
                                                            checked={!!item.campaign?.invoices?.[0]?.isPaid}
                                                            label={
                                                                item.campaign?.invoices?.[0]?.isPaid ? 'Paid' : 'Unpaid'
                                                            }
                                                        />
                                                    ) : (
                                                        ''
                                                    )}
                                                </td>
                                                <td>
                                                    <div>
                                                        {item.campaign?.invoices?.[0]?.isPaid &&
                                                        item.campaign?.invoices?.[0]?.paidAt
                                                            ? formatDate(item.campaign?.invoices?.[0]?.paidAt)
                                                            : ''}
                                                    </div>
                                                </td>
                                                <td>
                                                    <DropdownButton title="Actions">
                                                        <Dropdown.Item
                                                            onClick={() =>
                                                                handleVerify(item?.campaign?.invoices?.[0]?.id)
                                                            }
                                                            disabled={
                                                                !item?.campaign?.invoices?.[0]?.id ||
                                                                !hasAccessRight(AdminAclEnum.INVOICE_VERIFICATION)
                                                            }
                                                        >
                                                            Open
                                                        </Dropdown.Item>
                                                    </DropdownButton>
                                                </td>
                                            </>
                                        }
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                <Pagination
                    page={pagination.page}
                    totalPages={totalPages}
                    handlePagination={handlePages}
                    disabled={dataLoading}
                />
            </Card>
        </div>
    );
}
